
import { Component, Prop, Vue, Watch } from "vue-property-decorator";

import NbNewUploaderDragAndDrop from "@/components/NewFileUploader/NbNewUploader/NbNewUploaderDragAndDrop.vue";
import NbNewUploaderFilesList from "@/components/NewFileUploader/NbNewUploader/NbNewUploaderFilesList.vue";
import NbNewUploaderPdfPreview from "@/components/NewFileUploader/NbNewUploader/NbNewUploaderPdfPreview.vue";

const MODEL_CLASS = "uploader";

const MESSAGES = {
  title: "Choose PDF files and preview them",
  buttons: {
    cancel: "Cancel",
    save: "Save",
  },
  errors: {
    norPdfFormat: "This file format isn't recognised. Please upload only PDF files.",
    fileSize: "This file has exceeded the allowed size. Please upload a smaller file.",
    alreadyUploaded: "Already has uploaded file! Remove previous file first.",
    filesLimitExceed: "You try to load more then 1 file, in single mode.",
    specificFileMode: "For specific document type, you can load only one file.",
  },
};

const ERROR_NO_PDF_FORMAT = "notPdfFormat";

const MAX_FILE_SIZE_IN_MB = 2;

const PDF_FILE_FORMAT = "pdf";
const AVAILABLE_FILES_FORMATS = [PDF_FILE_FORMAT];

const SAVED_FILE_STATUS = "saved";
const CONFIRMED_FILE_STATUS = "confirmed";

const COS_DOCUMENT_TYPE = "cos";
const IELTS_DOCUMENT_TYPE = "ielts";
const PEARSON_DOCUMENT_TYPE = "pte";

const SPECIFIC_DOCUMENT_TYPE = [COS_DOCUMENT_TYPE, IELTS_DOCUMENT_TYPE, PEARSON_DOCUMENT_TYPE];

const DROP_ERROR_DISPLAY_DURATION = 5000;

const DRAGLEAVE_STATE = "dragleave";
const DRAGLEAVE_TIMEOUT = 0;

@Component({
  components: {
    NbNewUploaderPdfPreview,
    NbNewUploaderFilesList,
    NbNewUploaderDragAndDrop,
  },
})
export default class NbNewUploader extends Vue {
  @Prop({ type: Boolean, default: false }) readonly visible!: boolean;
  @Prop({ type: Boolean, default: false }) readonly stretch!: boolean;
  @Prop({ type: Boolean, default: false }) readonly absolute!: boolean;
  @Prop({ type: String, default: "" }) readonly label!: string;
  @Prop({ type: Array, default: () => AVAILABLE_FILES_FORMATS }) readonly fileTypes?: string;
  @Prop({ type: Number, default: MAX_FILE_SIZE_IN_MB }) readonly maxFileSize?: number;
  @Prop({ type: Function, default: () => ({}) }) readonly saveMethod?: Function;
  @Prop({ type: Boolean, default: true }) readonly multiple?: boolean;
  @Prop({ type: String, default: "" }) readonly documentType!: string;
  @Prop({ type: Array, default: () => [] }) readonly files?;
  @Prop({ type: Boolean, default: false }) readonly closeOnSave?: boolean;
  @Prop({ type: Boolean, default: true }) readonly cleanOnCancel?: boolean;
  @Prop({ type: Boolean, default: false }) readonly showLoadErrors?: boolean;
  @Prop({ type: Function, default: () => ({}) }) readonly confirmMethod?: Function;
  @Prop({ type: Boolean, default: false }) readonly parsingAsObject?: boolean;

  MESSAGES = MESSAGES;

  state = "";

  get classes() {
    const { stretch, absolute } = this;

    return {
      [`${MODEL_CLASS}_stretch`]: stretch,
      [`${MODEL_CLASS}_absolute`]: absolute,
    };
  }

  get displayFilesList() {
    return this.hasUploadedFiles && !this.isDragStarted;
  }

  get displayDropErrors() {
    return this.showLoadErrors && this.dropErrors.length && !this.isDragStarted;
  }

  get displayDropDown() {
    return !this.hasUploadedFiles || this.multipleEnable || this.isDragStarted;
  }

  get isSpecificDocumentType() {
    return SPECIFIC_DOCUMENT_TYPE.includes(this.documentType);
  }

  get multipleEnable() {
    return this.multiple && !this.isSpecificDocumentType;
  }

  cancel() {
    this.$emit("update:visible", false);

    this.$emit("cancel");
  }

  clean() {
    this.uploadedFiles = [];

    this.selectedFile = "";
    this.selectedFileIndex = "";
  }

  @Watch("visible")
  visibleChanged(value: boolean) {
    if (value) return;

    if (this.cleanOnCancel) this.clean();
  }

  getErrorFromResponse(error) {
    const { status, data } = error.response;

    return {
      error: status,
      ...data,
    };
  }

  changeState(value: string) {
    const timeout = value === DRAGLEAVE_STATE ? DRAGLEAVE_TIMEOUT : 0;

    setTimeout(() => {
      this.state = value;
    }, timeout);
  }

  get isDragStarted() {
    return this.state === "dragstart";
  }

  drop(payload) {
    payload.preventDefault();

    this.changeState(DRAGLEAVE_STATE);

    const files = payload.dataTransfer.files;

    this.upload(files);
  }

  updateSavedFile(payload, file) {
    Object.assign(file, {
      id: payload.ID,
      loading: false,
      status: SAVED_FILE_STATUS,
    });
  }

  isEnableToSave(file) {
    return !file.errors.length && file.status !== SAVED_FILE_STATUS;
  }

  uploadedData = "";

  async save() {
    this.$emit("save", this.uploadedFiles);

    for await (const [index, value] of this.uploadedFiles.entries()) {
      try {
        this.uploadedFiles[index].loading = true;

        if (this.isEnableToSave(this.uploadedFiles[index])) {
          if (!this.saveMethod) return;

          let { data } = await this.saveMethod(value.data);

          data = this.parsingAsObject || this.isSpecificDocumentType ? data : data[0];

          this.uploadedData = data;

          this.updateSavedFile(data, value);
        }
      } catch (error) {
        this.uploadedFiles[index].errors = [];

        const err = this.getErrorFromResponse(error);

        if (!err) return;

        this.uploadedFiles[index].errors.push(err);
      } finally {
        this.uploadedFiles[index].loading = false;
      }
    }

    this.updateAllFilesSaved();

    if (this.isAllFilesSaved && this.closeOnSave) {
      this.cancel();
    }

    this.$emit("success", this.isAllFilesSaved);
  }

  getSavedFilesIds() {
    return this.uploadedFiles.reduce((acc, cur) => {
      if (cur.status !== SAVED_FILE_STATUS) return acc;

      acc.push(cur.id);

      return acc;
    }, []);
  }

  loading = false;

  async confirm() {
    try {
      this.loading = true;

      if (!this.confirmMethod) return;

      const ids = this.getSavedFilesIds();

      const payload = await this.confirmMethod(ids, this.uploadedData);

      this.confirmUploadedFiles(ids);

      this.$emit("confirm", { ...payload, success: true });
    } finally {
      this.loading = false;
    }
  }

  confirmUploadedFiles(ids) {
    this.uploadedFiles.map((file) => {
      if (ids.includes(file.id)) {
        file.status = CONFIRMED_FILE_STATUS;
      }

      return file;
    });
  }

  get hasUploadedFiles() {
    return !!this.uploadedFiles.length;
  }

  uploadedFiles = [];

  getFileExtension(file) {
    if (file instanceof File) {
      const typeParts = file.type.split("/");

      return typeParts[typeParts.length - 1];
    }

    const url = URL.createObjectURL(file);

    return url
      .split(/[#?]/)[0]
      .split(".")
      .pop()
      .trim();
  }

  checkIsAvailableExtension(file) {
    const extension = this.getFileExtension(file);

    return this.fileTypes.includes(extension);
  }

  getFileErrors(file) {
    const errors = [];

    const availableFileTypeForUpload = this.checkIsAvailableExtension(file);

    if (!availableFileTypeForUpload) {
      errors.push({
        error: ERROR_NO_PDF_FORMAT,
        message: MESSAGES.errors.norPdfFormat,
      });
    }

    return errors;
  }

  generateFileObject(file) {
    return {
      id: null,
      data: file,
      meta: [],
      errors: this.getFileErrors(file),
      loading: false,
    };
  }

  get uploadLoading() {
    return this.uploadedFiles.some((file) => file.loading);
  }

  upload(transferredFiles) {
    if (!transferredFiles.length) return;

    if (this.multipleEnable) {
      for (let i = 0; i < transferredFiles.length; i++) {
        const fileItem = this.generateFileObject(transferredFiles[i]);

        this.uploadedFiles = [fileItem, ...this.uploadedFiles];
      }
    } else {
      if (transferredFiles.length > 1) {
        const message = this.isSpecificDocumentType
          ? MESSAGES.errors.specificFileMode
          : MESSAGES.errors.filesLimitExceed;

        this.showDropError(message);
      }

      this.uploadedFiles = [];

      const fileItem = this.generateFileObject(transferredFiles[0]);

      this.uploadedFiles.push(fileItem);
    }

    this.save();
  }

  isAllFilesSaved = false;

  updateAllFilesSaved() {
    this.isAllFilesSaved = this.uploadedFiles?.every((file) => file?.status === SAVED_FILE_STATUS);
  }

  isHasNotSavedFiles = false;

  updateHasNotSavedFiles() {
    this.isHasNotSavedFiles = this.uploadedFiles?.some(
      (file) => file?.status !== SAVED_FILE_STATUS
    );
  }

  isHasNotConfirmedFiles = false;

  updateHasNotConfirmedFiles() {
    this.isHasNotConfirmedFiles = this.uploadedFiles?.some(
      (file) => file?.status !== CONFIRMED_FILE_STATUS
    );
  }

  @Watch("uploadedFiles", { immediate: true, deep: true })
  onFilesChanges(payload) {
    this.$emit("update:files", payload);

    this.updateAllFilesSaved();

    this.updateHasNotSavedFiles();

    this.updateHasNotConfirmedFiles();
  }

  get filesHasErrors() {
    return this.uploadedFiles?.some((file) => file?.errors.length);
  }

  get saveIsDisabled() {
    return (
      !this.uploadedFiles.length ||
      this.filesHasErrors ||
      !this.isHasNotConfirmedFiles ||
      this.uploadLoading
    );
  }

  selectedFile = "";
  selectedFileIndex = "";

  select({ payload, index }) {
    this.selectedFile = payload;
    this.selectedFileIndex = index;
  }

  reUpload(payload) {
    this.uploadedFiles[this.selectedFileIndex] = this.generateFileObject(payload);

    const newFiles = [...this.uploadedFiles];

    this.uploadedFiles = [];

    this.$forceUpdate();

    this.uploadedFiles = newFiles;

    this.save();

    this.select({
      payload: this.uploadedFiles[this.selectedFileIndex],
      index: this.selectedFileIndex,
    });
  }

  deleteFile({ index }) {
    this.uploadedFiles.splice(index, 1);

    const newFiles = [...this.uploadedFiles];

    this.$forceUpdate();

    this.uploadedFiles = newFiles;

    this.select({
      payload: "",
      index: "",
    });
  }

  dropErrors = [];

  showDropError(payload) {
    const newItemIndex = this.dropErrors.length;

    this.dropErrors.push(payload);

    setTimeout(() => {
      this.dropErrors.splice(newItemIndex, 1);
    }, DROP_ERROR_DISPLAY_DURATION);
  }
}
